import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createVNode as _createVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "mb-4 text-gray-600" }
const _hoisted_2 = { class: "mb-4 flex items-center gap-2 text-sm" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", null, "/", -1)
const _hoisted_4 = ["onClick"]
const _hoisted_5 = {
  key: 0,
  class: "border rounded-lg max-h-64 overflow-y-auto mb-4"
}
const _hoisted_6 = ["onClick", "onDblclick"]
const _hoisted_7 = {
  key: 1,
  class: "text-gray-500 mb-4"
}
const _hoisted_8 = { class: "mb-4 text-sm text-gray-700" }
const _hoisted_9 = { class: "flex gap-3" }

import { ref, watch, computed } from "vue";
import { FolderIcon } from "@heroicons/vue/outline";
import Modal from "@/components/common/Modal.vue";
import Button from "@/components/common/Button.vue";
import { DocumentFolderDataService } from "@/services";
import { DocumentFolder } from "@/types/documents/folder/DocumentFolder";
import { useToast } from "vue-toastification";
import { t } from "@/i18n";


export default /*#__PURE__*/_defineComponent({
  props: {
  show: { type: Boolean, default: false },
  checkedDocumentIds: {
    type: Array as () => string[],
    default: () => [],
  },
  checkedFolderIds: {
    type: Array as () => string[],
    default: () => [],
  },
},
  setup(__props, { emit }: { emit: ({ hide: []; submit: [] }), expose: any, slots: any, attrs: any }) {

const props = __props





const toast = useToast();
const loading = ref(false);
const targetFolderId = ref<string | null>(null);
const folders = ref<DocumentFolder[]>([]);
const breadcrumbs = ref<{ id: string; title: string }[]>([]);

const targetLabel = computed(() => {
  if (targetFolderId.value === null) return t("archive.root");
  const folder = folders.value.find((f) => f.id === targetFolderId.value);
  if (folder) return folder.title;
  const crumb = breadcrumbs.value.find((b) => b.id === targetFolderId.value);
  return crumb?.title ?? String(targetFolderId.value);
});

async function loadFolders(folderId: string | null) {
  const response = await DocumentFolderDataService.list(folderId, 0, 100);
  folders.value = response.data.data.folders;
}

async function navigateTo(folderId: string | null) {
  targetFolderId.value = folderId;
  if (folderId === null) {
    breadcrumbs.value = [];
  } else {
    try {
      const response = await DocumentFolderDataService.view(folderId);
      const folder = response.data.data;
      const existingIndex = breadcrumbs.value.findIndex(
        (b) => b.id === folderId
      );
      if (existingIndex >= 0) {
        breadcrumbs.value = breadcrumbs.value.slice(0, existingIndex + 1);
      } else {
        breadcrumbs.value.push({ id: folder.id, title: folder.title });
      }
    } catch {
      // ignore view errors during navigation
    }
  }
  await loadFolders(folderId);
}

function selectFolder(folder: DocumentFolder) {
  targetFolderId.value = folder.id;
}

async function confirmMove() {
  loading.value = true;
  try {
    const response = await DocumentFolderDataService.changeLocation({
      folderId: targetFolderId.value,
      checkedDocumentData: props.checkedDocumentIds,
      checkedFolderData: props.checkedFolderIds,
    });
    toast.success(response.data.message);
    emit("submit");
    emit("hide");
  } catch (error: any) {
    toast.error(error.response?.data?.message || t("common.error"));
  } finally {
    loading.value = false;
  }
}

watch(
  () => props.show,
  async (visible) => {
    if (visible) {
      targetFolderId.value = null;
      breadcrumbs.value = [];
      await loadFolders(null);
    }
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Modal, {
    show: __props.show,
    title: _unref(t)('archive.move'),
    onClose: _cache[2] || (_cache[2] = ($event: any) => (emit('hide')))
  }, {
    default: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, _toDisplayString(_unref(t)("archive.moveHint")), 1),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("button", {
          type: "button",
          class: "text-green-600 hover:underline",
          onClick: _cache[0] || (_cache[0] = ($event: any) => (navigateTo(null)))
        }, _toDisplayString(_unref(t)("archive.root")), 1),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(breadcrumbs.value, (crumb) => {
          return (_openBlock(), _createElementBlock(_Fragment, {
            key: crumb.id
          }, [
            _hoisted_3,
            _createElementVNode("button", {
              type: "button",
              class: "text-green-600 hover:underline",
              onClick: ($event: any) => (navigateTo(crumb.id))
            }, _toDisplayString(crumb.title), 9, _hoisted_4)
          ], 64))
        }), 128))
      ]),
      (folders.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(folders.value, (folder) => {
              return (_openBlock(), _createElementBlock("button", {
                key: folder.id,
                type: "button",
                class: _normalizeClass(["flex items-center w-full px-4 py-2 text-left hover:bg-gray-50", { 'bg-green-50': targetFolderId.value === folder.id }]),
                onClick: ($event: any) => (selectFolder(folder)),
                onDblclick: ($event: any) => (navigateTo(folder.id))
              }, [
                _createVNode(_unref(FolderIcon), { class: "w-5 h-5 mr-2 text-gray-500" }),
                _createTextVNode(" " + _toDisplayString(folder.title), 1)
              ], 42, _hoisted_6))
            }), 128))
          ]))
        : (_openBlock(), _createElementBlock("p", _hoisted_7, _toDisplayString(_unref(t)("archive.noFolders")), 1)),
      _createElementVNode("p", _hoisted_8, [
        _createTextVNode(_toDisplayString(_unref(t)("archive.moveTarget")) + ": ", 1),
        _createElementVNode("strong", null, _toDisplayString(_unref(targetLabel)), 1)
      ]),
      _createElementVNode("div", _hoisted_9, [
        _createVNode(Button, {
          type: "button",
          onClick: _cache[1] || (_cache[1] = ($event: any) => (emit('hide')))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("common.cancel")), 1)
          ]),
          _: 1
        }),
        _createVNode(Button, {
          type: "button",
          disabled: loading.value,
          onClick: confirmMove
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(_unref(t)("archive.move")), 1)
          ]),
          _: 1
        }, 8, ["disabled"])
      ])
    ]),
    _: 1
  }, 8, ["show", "title"]))
}
}

})